/**
 * EF-EMAIL-SUBJECT-ROUTER-V1 + UI-ZOOM-EXTENSION
 * 
 * Reusable zoom and text wrap control component.
 */
import React from 'react';
import { ZoomIn, ZoomOut, WrapText, AlignLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from '../ui/tooltip';

/**
 * Zoom control buttons with optional text wrap toggle
 * 
 * @param {object} props
 * @param {string} props.currentLabel - Current zoom level label (e.g., "100%")
 * @param {function} props.onZoomIn - Zoom in handler
 * @param {function} props.onZoomOut - Zoom out handler
 * @param {boolean} props.canZoomIn - Whether can zoom in more
 * @param {boolean} props.canZoomOut - Whether can zoom out more
 * @param {boolean} props.wrapText - Current wrap state (optional)
 * @param {function} props.onToggleWrap - Toggle wrap handler (optional)
 * @param {boolean} props.showWrapControl - Show wrap toggle (default: true)
 * @param {string} props.className - Additional classes
 */
export function ZoomControl({
  currentLabel,
  onZoomIn,
  onZoomOut,
  canZoomIn,
  canZoomOut,
  wrapText,
  onToggleWrap,
  showWrapControl = true,
  className = "",
}) {
  return (
    <TooltipProvider>
      <div className={`flex items-center gap-1 ${className}`}>
        {/* Zoom Out */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={onZoomOut}
              disabled={!canZoomOut}
              className="h-7 w-7 p-0"
              data-testid="zoom-out-btn"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Zoom Out (Compact)</p>
          </TooltipContent>
        </Tooltip>

        {/* Current Level Label */}
        <span 
          className="text-xs text-zinc-400 min-w-[50px] text-center"
          data-testid="zoom-level-label"
        >
          {currentLabel}
        </span>

        {/* Zoom In */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={onZoomIn}
              disabled={!canZoomIn}
              className="h-7 w-7 p-0"
              data-testid="zoom-in-btn"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Zoom In (Large)</p>
          </TooltipContent>
        </Tooltip>

        {/* Separator */}
        {showWrapControl && (
          <>
            <div className="w-px h-4 bg-zinc-700 mx-1" />
            
            {/* Text Wrap Toggle */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={wrapText ? "secondary" : "ghost"}
                  size="sm"
                  onClick={onToggleWrap}
                  className="h-7 w-7 p-0"
                  data-testid="wrap-text-btn"
                >
                  {wrapText ? (
                    <WrapText className="h-4 w-4" />
                  ) : (
                    <AlignLeft className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{wrapText ? "Text Wrap: ON" : "Text Wrap: OFF"}</p>
              </TooltipContent>
            </Tooltip>
          </>
        )}
      </div>
    </TooltipProvider>
  );
}

export default ZoomControl;
